import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Link as RouterLink } from 'react-router-dom';
import { DriverDashboardSummary } from '../types';
import { fetchDriverDashboardSummary } from '../services/driverWorkspaceService';
import api from '../services/api';
import { requestAndRegisterToken } from '../services/notificationService';
import { HeroBannerSkeleton } from '../components/UI/PageSkeleton';

const Onboarding: React.FC = () => {
  const [summary, setSummary] = useState<DriverDashboardSummary | null>(null);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    const response = await fetchDriverDashboardSummary();
    setSummary(response);
    setPhone(response.profile.phone || '');
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const savePhone = async () => {
    if (!phone) return;
    setSaving(true);
    try {
      await api.put('/api/users/profile', { name: summary?.profile.name, phone });
      await load();
      setMessage('Phone number saved.');
    } catch {
      setMessage('Unable to save phone number.');
    } finally {
      setSaving(false);
    }
  };

  const enablePush = async () => {
    setSaving(true);
    try {
      const token = await requestAndRegisterToken();
      await load();
      setMessage(token ? 'Push notifications enabled.' : 'Notifications not available or denied.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack spacing={3} sx={{ maxWidth: 980, mx: 'auto' }}>
        <HeroBannerSkeleton />
        <Paper variant="outlined" sx={{ p: 3, height: 300 }}>
          <LinearProgress />
        </Paper>
      </Stack>
    );
  }

  if (!summary) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography mb={2}>Unable to load onboarding.</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Reload</Button>
      </Paper>
    );
  }

  const steps = [
    {
      label: 'Add your phone number',
      done: summary.onboarding.profileComplete,
      description: 'Your phone number is used for account recovery and support communication.',
      action: (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={1}>
          <TextField
            label="Phone number"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+91 98765 43210"
            size="small"
            sx={{ flex: 1 }}
          />
          <Button variant="contained" onClick={savePhone} disabled={saving || !phone}>
            Save
          </Button>
        </Stack>
      ),
    },
    {
      label: 'Set your route preferences',
      done: summary.onboarding.preferencesComplete,
      description: 'Tell us your preferred pickup areas, drop zones and vehicle types so we can rank the best matches in your feed.',
      action: (
        <Button component={RouterLink} to="/settings?tab=preferences" variant="outlined" size="small" sx={{ mt: 1 }}>
          Add pickup / drop filters
        </Button>
      ),
    },
    {
      label: 'Enable push notifications',
      done: summary.onboarding.notificationsEnabled,
      description: 'Get instant alerts when a new ride matching your preferences is posted.',
      action: (
        <Button variant="outlined" size="small" onClick={enablePush} disabled={saving} sx={{ mt: 1 }}>
          Enable on this device
        </Button>
      ),
    },
    {
      label: 'Browse your first lead',
      done: true,
      description: 'Visit the live ride workspace to see the current feed and explore how match scoring works.',
      action: (
        <Button component={RouterLink} to="/app/rides" variant="outlined" size="small" sx={{ mt: 1 }}>
          Open ride workspace
        </Button>
      ),
    },
    {
      label: 'Unlock your first ride',
      done: summary.onboarding.firstLeadUnlocked,
      description: 'Full ride details stay hidden until you spend one credit. Open a ride, check the route and match reasons, then unlock when it looks promising.',
      action: null,
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);
  const allDone = completedCount === steps.length;

  return (
    <Stack spacing={3} sx={{ maxWidth: 980, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{ p: { xs: 2.5, md: 3 }, bgcolor: '#061b31', color: 'white', background: 'linear-gradient(135deg,#061b31,#0b3156)' }}
      >
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Box>
            <Typography variant="h4" fontWeight={900}>Set up your driver workspace</Typography>
            <Typography color="rgba(255,255,255,0.72)" mt={0.75}>
              {completedCount} of {steps.length} steps complete — complete setup to get the most relevant rides first.
            </Typography>
          </Box>
          <Chip
            label={`${progressPercent}%`}
            sx={{ bgcolor: allDone ? '#27c46b' : '#ffc928', color: '#07182d', fontWeight: 900, fontSize: 16, height: 36, minWidth: 64 }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{ mt: 2, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', '& .MuiLinearProgress-bar': { bgcolor: allDone ? '#27c46b' : '#ffc928' } }}
        />
      </Paper>

      {allDone && (
        <Alert severity="success" icon={<CheckCircleIcon />}>
          All setup steps complete! Your workspace is fully configured.
        </Alert>
      )}

      {message && (
        <Alert severity={message.includes('Unable') ? 'error' : 'success'} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stepper orientation="vertical" nonLinear>
          {steps.map((step) => (
            <Step key={step.label} active completed={step.done}>
              <StepLabel
                StepIconComponent={() =>
                  step.done
                    ? <CheckCircleIcon color="success" sx={{ fontSize: 24 }} />
                    : <RadioButtonUncheckedIcon sx={{ fontSize: 24, color: 'rgba(7,24,45,0.3)' }} />
                }
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={step.done ? 700 : 600}
                    color={step.done ? 'text.primary' : 'text.secondary'}
                  >
                    {step.label}
                  </Typography>
                  {step.done && (
                    <Chip label="Done" color="success" size="small" />
                  )}
                </Box>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
                  {step.description}
                </Typography>
                {!step.done && step.action}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Box>
        <Button component={RouterLink} to="/app" variant="contained" color="secondary">
          Go to dashboard
        </Button>
      </Box>
    </Stack>
  );
};

export default Onboarding;
